import './index.css'
import { newlinesAsHtml } from '@disco/utils/newlines'
import { pushUINotification } from '@disco/utils/notifications'
import clsx from 'clsx'

import React, { useContext, useState } from 'react'
import CopyToClipboard from 'react-clipboard.js'
import { useDispatch } from 'hooks/use-redux'
import type { Track } from 'types/track'
import { ThemeContext } from 'contexts/theme'
import { convertBreakCharsToLineBreaks } from 'utils/dom'
import TrackTooltip from 'components/track-tooltip'
import Button from 'components/ui/button'
import DiscoModal from 'components/ui/disco-modal'
import Heading from 'components/ui/heading'
import Hotkey from 'components/ui/hotkey'
import modalStyles from 'components/utils/modalStyles'
import PlaylistMessage from 'pages/public-playlist/playlist/playlist-message'
import { Trans } from '@disco/i18n/trans'
import { useTranslation } from '@disco/i18n/use-translation'
type Props = {
  showLyrics: boolean
  showMetadata: boolean | null | undefined
  track: Track
  metadataSubset: string[] | null | undefined
}

const TrackDataInOneModal = ({
  track,
  showLyrics,
  showMetadata,
  metadataSubset,
}: Props): null | React.ReactNode => {
  const { t } = useTranslation('shares')
  const dispatch = useDispatch()
  const [showModal, setShowModal] = useState(false)
  const [copyToClipboardTooltip, setCopyToClipboardTooltip] =
    useState<React.ReactNode>(t('shares:copyLyrics', 'Copy Lyrics'))
  const theme = useContext(ThemeContext)?.theme
  const lyrics = track.metadata?.Lyrics
  const comments = track.comments || []

  if (!(showLyrics && lyrics) && !comments.length && !showMetadata) {
    return null
  }

  return (
    <>
      <Button
        appearance="icon"
        icon="info-outline"
        onClick={() => setShowModal(true)}
      />
      <DiscoModal
        style={{
          overlay: { ...modalStyles.overlay, zIndex: 109, height: '100vh' },
          content: { ...modalStyles.content, margin: 'auto' },
        }}
        onRequestClose={() => setShowModal(false)}
        isOpen={showModal}
        showCloseButton
      >
        <div
          className={clsx('TrackDataInOneModal', {
            'TrackDataInOneModal--ThemeDark': theme === 'dark',
            'TrackDataInOneModal--ThemeLight': theme === 'light',
          })}
        >
          <Heading size={600}>{track.name}</Heading>
          {Boolean(comments.length) && (
            <div className="TrackDataInOneModal_Section">
              <div className="TrackDataInOneModal_Heading">
                <Heading size={400}>{t('shares:notes', 'Notes')}</Heading>
              </div>
              <div className="TrackDataInOneModal_Text">
                {comments.map(({ comment }, index) => (
                  <div key={`${comment}-${index}`}>
                    {convertBreakCharsToLineBreaks(comment)}
                  </div>
                ))}
              </div>
            </div>
          )}
          {showLyrics && lyrics && (
            <div className="TrackDataInOneModal_Section">
              <div className="TrackDataInOneModal_Heading">
                <div>
                  <Heading size={400}>{t('shares:lyrics', 'Lyrics')}</Heading>
                </div>
                <CopyToClipboard
                  component="div"
                  data-clipboard-text={lyrics}
                  onError={() => {
                    setCopyToClipboardTooltip(
                      <span>
                        <Trans
                          t={t}
                          i18nKey="shares:pressToCopy"
                          defaults="Press <shortcut></shortcut> to copy"
                          components={{
                            shortcut: <Hotkey keys={['cmd', 'C']} />,
                          }}
                        />
                      </span>,
                    )
                    setTimeout(() => {
                      setCopyToClipboardTooltip(
                        t('shares:copyLyrics', 'Copy Lyrics'),
                      )
                    }, 3000)
                  }}
                  onSuccess={() => {
                    pushUINotification({
                      message: t('shares:copied', 'Copied!'),
                      level: 'success',
                    })
                  }}
                >
                  <div className="TrackDataInOneModal_CopyButton">
                    <Button
                      appearance="icon"
                      icon="copy"
                      tooltip={copyToClipboardTooltip}
                    />
                  </div>
                </CopyToClipboard>
              </div>
              <div className="TrackDataInOneModal_Text">
                <PlaylistMessage // TODO: replace newlinesAsHtml() with newlines() and figure how to truncate better
                  message={newlinesAsHtml(lyrics)}
                  trimLength={113}
                  maxLengthBeforeTrim={113}
                  CustomButton={({ onClick, caption }) => (
                    <span
                      className="TrackDataInOneModal_Button"
                      onClick={onClick}
                    >
                      {caption}
                    </span>
                  )}
                  isHtml
                />
              </div>
            </div>
          )}

          {showMetadata && (
            <div className="TrackDataInOneModal_Section">
              <div className="TrackDataInOneModal_Heading">
                <Heading size={400}>{t('shares:metadata', 'Metadata')}</Heading>
              </div>
              <div className="TrackDataInOneModal_Text">
                <TrackTooltip
                  track={track}
                  showViewLink={false}
                  metadataSubset={metadataSubset}
                  returnOnlyContent
                />
              </div>
            </div>
          )}
        </div>
      </DiscoModal>
    </>
  )
}

export default TrackDataInOneModal
